/**
 * Sandbox knob catalog — the nine sliders rendered by `<KnobGrid>` in
 * `components/pilot/Sandbox.tsx`. Each entry binds a knob id (matching
 * a key in `SimParams` from `./params.ts`) to its UI metadata: label,
 * range, step, default, formatter, hint, and an optional "high-leverage"
 * accent flag.
 *
 * Order is intentional: highest-leverage dollar levers first
 * (`hourlyRate`, `procsPerYear`), then "what's broken today" reality
 * knobs, then "how much can Planapus help" coverage knobs.
 *
 * Extracted from the soon-to-die `lib/pilot/render.js` and `main.js`
 * during Phase 5 of the React-engine port (see
 * `docs/eng/pilot-react-engine.md`).
 */

import { HOURLY_RATE, PROCS_PER_YEAR } from "./processes";
import type { ParamId } from "./params";

/** Scope of a knob — distinguishes process-wide dials (apply across the
 * whole run) from per-step dials (apply at every handoff). Surfaced as
 * a section header in the Sandbox per the May 2026 review feedback that
 * the dial list felt undifferentiated. */
export type KnobScope = 'process' | 'step';

/** UI metadata for one Sandbox knob. */
export interface KnobMeta {
  id: ParamId;
  label: string;
  min: number;
  max: number;
  step: number;
  def: number;
  /** Format the live value for display in the knob's `<span class="val">`. */
  fmt: (v: number) => string;
  /** One-sentence explanation shown under the slider. */
  hint: string;
  /** When true, surface a "high-leverage" badge to flag the lever. */
  accent?: boolean;
  /** Whether this dial applies process-wide or per-step. */
  scope: KnobScope;
}

export const KNOBS: ReadonlyArray<KnobMeta> = [
  // Process-wide dials — apply across the whole run.
  {
    id: "hourlyRate",
    label: "Loaded hourly cost",
    min: 50,
    max: 500,
    step: 10,
    def: HOURLY_RATE,
    fmt: (v) => `$${v}/hr`,
    hint: "Fully-loaded knowledge-worker cost — salary + benefits + overhead. Median in the US is $130–$180/hr. This moves the dollar figure 1:1.",
    accent: true,
    scope: 'process',
  },
  {
    id: "procsPerYear",
    label: "Runs per year",
    min: 5,
    max: 500,
    step: 5,
    def: 50,
    fmt: (v) => `${Math.round(v)}/yr`,
    hint: "How many of these processes your org runs each year. The whole dollar figure scales linearly with this.",
    accent: true,
    scope: 'process',
  },
  {
    id: "overlap",
    label: "Working-hours overlap",
    min: 0.2,
    max: 1.0,
    step: 0.05,
    def: 0.7,
    fmt: (v) => `${Math.round(v * 100)}%`,
    hint: "Fraction of time both parties are awake and working. ~70% for same-coast teams; ~30% for fully distributed.",
    scope: 'process',
  },
  {
    id: "automationPct",
    label: "Automation potential",
    min: 0.0,
    max: 1.0,
    step: 0.05,
    def: 0.7,
    fmt: (v) => `${Math.round(v * 100)}%`,
    hint: "Share of process steps that can be automated at all. At 100%, every step is fair game for Planapus.",
    scope: 'process',
  },
  {
    id: "aiCoverage",
    label: "Planapus coverage",
    min: 0.0,
    max: 1.0,
    step: 0.05,
    def: 0.9,
    fmt: (v) => `${Math.round(v * 100)}%`,
    hint: "Of the automatable steps, the share Planapus actually orchestrates.",
    scope: 'process',
  },
  // Per-step dials — apply at every handoff in the run.
  {
    id: "avgResponseHrs",
    label: "Average response time",
    min: 2,
    max: 120,
    step: 1,
    def: 24,
    fmt: (v) => `${Math.round(v)}h`,
    hint: "Typical wait on a handoff before someone responds. 24h is one full business day.",
    scope: 'step',
  },
  {
    id: "errorRate",
    label: "Error / rework rate",
    min: 0.0,
    max: 0.3,
    step: 0.01,
    def: 0.08,
    fmt: (v) => `${Math.round(v * 100)}%`,
    hint: "Probability a handoff arrives with a mistake and bounces. 5–15% is typical.",
    scope: 'step',
  },
  {
    id: "oooRate",
    label: "Out-of-office probability",
    min: 0.0,
    max: 0.3,
    step: 0.01,
    def: 0.06,
    fmt: (v) => `${Math.round(v * 100)}%`,
    hint: "Per-handoff chance someone is on PTO. 5–10% reflects normal vacation density.",
    scope: 'step',
  },
  {
    id: "dropRate",
    label: "Ghost / drop-off rate",
    min: 0.0,
    max: 0.05,
    step: 0.005,
    def: 0.01,
    fmt: (v) => `${(v * 100).toFixed(1)}%`,
    hint: "Per-handoff chance the process dies entirely (deal lost, candidate ghosts).",
    scope: 'step',
  },
] as const;

/** Default knob value map keyed by knob id. */
export const KNOB_DEFAULTS: Readonly<Record<ParamId, number>> = Object.freeze(
  KNOBS.reduce(
    (acc, k) => {
      acc[k.id] = k.def;
      return acc;
    },
    {} as Record<ParamId, number>,
  ),
);
